import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { COMPANY } from "@/lib/site";

const years = new Date().getFullYear() - COMPANY.foundingYear;

const highlights = [
  "Global sourcing — APIs, raw materials & formulations",
  "USFDA, EU-GMP, MHRA audit-readiness expertise",
  "Advanced characterization — Malvern, PXRD, DSC, TGA, LCMS",
  "Stability, microbiological & impurity control programs",
  "Global dossier management across regulated markets",
  "QMS investigation, CAPA & data-integrity training",
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/9574352/pexels-photo-9574352.jpeg"
                alt="Scientist in sterile laboratory corridor"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 hidden md:block bg-teal-700 text-white p-6 w-56">
                <div className="font-display text-4xl font-semibold leading-none">{years}+</div>
                <div className="text-sm mt-2 opacity-90">
                  years of serving regulated pharma markets.
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 order-1 lg:order-2"
          >
            <div className="eyebrow mb-4">About Xpertee</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
              A quiet, credible force behind global pharma supply chains.
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              We help pharmaceutical manufacturers, biotechs and generics companies secure quality raw materials,
              stay ahead of regulatory expectations, and validate every step of their process — from bench to batch
              release.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Under the leadership of <strong className="text-slate-800 font-semibold">Manoj Kumar (Director)</strong>,
              our team combines technical depth with operational discipline — so you get answers grounded in science,
              defensible in front of auditors, and delivered on time.
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-1 inline-flex items-center justify-center w-5 h-5 bg-teal-50 text-teal-700 border border-teal-200">
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                  <span className="text-sm leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
